import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { SelectProjectGroupComponent } from '../../project/select-project-group/select-project-group.component';
import { C4ListResponse, ProjectGroupResponse, ProjectGroup, RestService } from 'cargo-chief-commons';
import { lastValueFrom } from 'rxjs';

// Default delay for the "Allocated Carriers" group (in minutes)
export const ALLOCATED_CARRIERS_GROUP_DELAY = 5;
export const ALLOCATED_CARRIERS_GROUP_NAME = 'Allocated Carriers';

@Injectable({
	providedIn: 'root',
})
export class ProjectGroupService {
	constructor(
		private utilService: UtilService,
		private dialog: MatDialog,
		private urlService: UrlService,
		private restService: RestService
	) {}

	async selectProjectGroup(
		projectId: string
	): Promise<{ projectGroupId?: string; projectHasNoGroups?: boolean } | null> {
		const url = this.urlService.getProjectGroups();
		const payload = {
			projectId,
			withProjectCarrierMaps: false,
		};
		const projectGroupListResponse: C4ListResponse<ProjectGroupResponse> = await lastValueFrom(
			this.restService.POST(url, payload)
		);
		const projectGroupList = (projectGroupListResponse && projectGroupListResponse.data) || [];
		const projectGroupsCount = projectGroupList.length;
		if (projectGroupsCount == 1) {
			return new Promise((resolve) => resolve({ projectGroupId: projectGroupList[0].id }));
		} else if (projectGroupsCount > 1) {
			const dialogRef = this.dialog.open(SelectProjectGroupComponent, {
				width: '500px',
				disableClose: true,
				data: { projectGroupList },
			});
			return dialogRef.afterClosed().toPromise();
		}
		return new Promise((resolve) => resolve({ projectHasNoGroups: true }));
	}

	/**
	 * Gets or creates the lowest priority group for allocated carriers.
	 * Per the Volume Based Allocation plan:
	 * - If multiple groups exist → return the lowest priority group (highest delay)
	 * - If only one group exists → create a new "Allocated Carriers" group with higher delay
	 * - If no groups exist → return projectHasNoGroups: true
	 *
	 * This ensures non-allocated carriers get first priority in the waterfall,
	 * while allocated carriers (who already got first shot during allocation)
	 * are contacted last if they declined or didn't respond.
	 */
	async getLowestPriorityGroup(
		projectId: string
	): Promise<{ projectGroupId?: string; projectHasNoGroups?: boolean; groupCreated?: boolean }> {
		const url = this.urlService.getProjectGroups();
		const payload = {
			projectId,
			withProjectCarrierMaps: false,
		};
		const projectGroupListResponse: C4ListResponse<ProjectGroupResponse> = await lastValueFrom(
			this.restService.POST(url, payload)
		);
		const projectGroupList = (projectGroupListResponse && projectGroupListResponse.data) || [];

		if (projectGroupList.length === 0) {
			return { projectHasNoGroups: true };
		}

		// If only one group exists, check if it's already the "Allocated Carriers" group
		if (projectGroupList.length === 1) {
			const existingGroup = projectGroupList[0];

			// If the existing group is already "Allocated Carriers", use it instead of creating a duplicate
			if (existingGroup.name === ALLOCATED_CARRIERS_GROUP_NAME) {
				return { projectGroupId: existingGroup.id, groupCreated: false };
			}

			// Create a new "Allocated Carriers" group with higher delay
			const newGroupDelay = Math.max(existingGroup.delayByMinutes + ALLOCATED_CARRIERS_GROUP_DELAY, ALLOCATED_CARRIERS_GROUP_DELAY);

			try {
				const newGroup = await this.createAllocatedCarriersGroup(projectId, newGroupDelay);

				if (newGroup?.id) {
					return { projectGroupId: newGroup.id, groupCreated: true };
				}
			} catch (error) {
				console.error('Failed to create Allocated Carriers group:', error);
				// Fall back to the existing group
			}

			return { projectGroupId: existingGroup.id };
		}

		// Multiple groups exist - find the one with the highest delayByMinutes (lowest priority)
		const lowestPriorityGroup = projectGroupList.reduce((lowest, current) => {
			return current.delayByMinutes > lowest.delayByMinutes ? current : lowest;
		}, projectGroupList[0]);

		return { projectGroupId: lowestPriorityGroup.id };
	}

	/**
	 * Creates a new "Allocated Carriers" group with the specified delay.
	 * This group is used to hold carriers with allocations so they get
	 * last priority in the waterfall.
	 */
	private async createAllocatedCarriersGroup(
		projectId: string,
		delayByMinutes: number
	): Promise<ProjectGroupResponse | null> {
		const createGroupPayload = {
			projectId,
			name: ALLOCATED_CARRIERS_GROUP_NAME,
			delayByMinutes,
			projectCarrierMapIdList: [], // Empty initially - carrier will be added separately
			withoutProjectCarrierMaps: true, // Required to create an empty group
		};

		try {
			const response: any = await lastValueFrom(
				this.restService.POST(this.urlService.createProjectGroups(), createGroupPayload)
			);

			return response?.data || null;
		} catch (error) {
			console.error('Failed to create Allocated Carriers group:', error);
			throw error;
		}
	}

	/**
	 * Select a group to move a carrier to, excluding the carrier's current group.
	 * Used when removing an allocation - the carrier needs to move to a different group.
	 *
	 * @param projectGroups - All project groups from the store
	 * @param excludeGroupId - The ID of the group to exclude (carrier's current group)
	 * @returns The selected group ID, or null if cancelled
	 */
	async selectGroupExcludingCurrent(
		projectGroups: ProjectGroup[],
		excludeGroupId: string
	): Promise<{ projectGroupId?: string; cancelled?: boolean }> {
		// Filter out system groups and the current group
		const availableGroups = projectGroups.filter(
			(group) => !group.systemGroup && group.id !== excludeGroupId
		);

		if (availableGroups.length === 0) {
			// No other groups available
			return { cancelled: true };
		}

		if (availableGroups.length === 1) {
			// Only one other group - select it automatically
			return { projectGroupId: availableGroups[0].id };
		}

		// Multiple groups - show dialog to let user choose
		const dialogRef = this.dialog.open(SelectProjectGroupComponent, {
			width: '500px',
			disableClose: true,
			data: { projectGroupList: availableGroups },
		});

		const result = await dialogRef.afterClosed().toPromise();

		if (result?.projectGroupId) {
			return { projectGroupId: result.projectGroupId };
		}

		return { cancelled: true };
	}
}
